import React from 'react';
import Localize from 'react-intl-universal';

import { get } from 'lodash-es';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { ADDRESS_OPTIONS } from '@common/Constants';
import { formatDDMMYYYHHMM } from '@common/Util';

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.booking.list,
  height: '100%',
  padding: '2rem 1rem 1rem 1rem',
  overflow: 'auto',

  [theme.breakpoints.up('xs')]: { height: 'max-content' },
  [theme.breakpoints.up('md')]: { height: 'max-content' },
  [theme.breakpoints.up('lg')]: { height: '67.3vh' }
}));

const StyledGrid = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '7px',
  marginBottom: '5px'
}));

const StyledHeader = styled(Typography)(({ theme }) => ({
  paddingTop: 0,
  fontSize: '1.7rem',
  fontWeight: 700,
  color: theme.palette.booking.header
}));

const StyledSubheader = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontWeight: '400',
  fontSize: '0.85rem'
}));

const paddingBasedOnScreenSize = {
  paddingLeft: { xs: '5px !important', sm: '16px' },
  paddingRight: { xs: '5px', sm: '16px' }
};

const SingleLineInfo = ({ label, text }) => (
  <Grid
    item
    xs={12}
    sm={12}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      lineHeight: '1.5rem',
      flexWrap: 'wrap'
    }}
  >
    <Typography component='span' fontWeight='bold' sx={{ flexShrink: 0 }}>
      {`${label}:`}
    </Typography>

    <Tooltip title={text}>
      <Typography component='span' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {text}
      </Typography>
    </Tooltip>
  </Grid>
);

const Summary = ({ participants = [], enableHotelRequest = false }) => {
  return (
    <StyledBox>
      <Box sx={{ display: 'flex', flexDirection: 'column', padding: 2, gap: 5 }}>
        {participants.length > 0 &&
          participants.map((participant, index) => (
            <StyledGrid key={index} container spacing={2} padding={{ xs: '5px', sm: '16px' }}>
              <Grid
                item
                xs={12}
                sm={12}
                sx={{ ...paddingBasedOnScreenSize, mb: '0px !important', pt: '0px !important' }}
              >
                <StyledHeader>
                  {`${get(participant, 'generalInformation.firstName', '')} ${get(participant, 'generalInformation.lastName', '')}`}
                </StyledHeader>
                <StyledSubheader>
                  {get(participant, 'isBookingPerson', false)
                    ? Localize.get('Booking.bookingPerson')
                    : Localize.get('Booking.participant')}
                </StyledSubheader>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  ...paddingBasedOnScreenSize,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <SingleLineInfo
                  label={Localize.get('Booking.emailAddress')}
                  text={
                    get(participant, 'communicationAddressRadio', '') ===
                    ADDRESS_OPTIONS.noAlternative
                      ? get(participant, 'contactInformation.email', '')
                      : get(participant, 'communicationAddress.email', '')
                  }
                />
                <SingleLineInfo
                  label={Localize.get('Booking.mobile')}
                  text={
                    get(participant, 'communicationAddressRadio', '') ===
                    ADDRESS_OPTIONS.noAlternative
                      ? get(participant, 'contactInformation.mobile', '')
                      : get(participant, 'communicationAddress.mobile', '')
                  }
                />
                <SingleLineInfo
                  label={Localize.get('Booking.company')}
                  text={get(participant, 'generalInformation.company', '')}
                />

                {enableHotelRequest ? (
                  <SingleLineInfo
                    label={Localize.get('Booking.hotelNeeded')}
                    text={
                      participant?.additionalInfo?.hotelRequest
                        ? Localize.get('App.yes') +
                          `, ${formatDDMMYYYHHMM(participant.additionalInfo?.arrivalDate, false)}` +
                          ` - ${formatDDMMYYYHHMM(participant.additionalInfo?.departureDate, false)}`
                        : Localize.get('App.no')
                    }
                  />
                ) : (
                  <></>
                )}
              </Grid>
            </StyledGrid>
          ))}
      </Box>
    </StyledBox>
  );
};
export default Summary;
